import React, { useMemo, useState } from "react";
import { useAuth } from "src/contexts/auth-provider";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { useReportQuery } from "src/features/Reports/hooks/useReportQuery";
import { getReportDateRange, type ReportDatePreset } from "src/features/Reports/utils/reportDateRange";
import type { ReportQueryInput, ReportsOverviewData, ReportsSalesSummaryData } from "src/features/Reports/types/report.types";
import { REPORTS_OVERVIEW, REPORTS_SALES_SUMMARY, REPORTS_SALES_SUMMARY_PREVIOUS } from "src/constants/queries";
import { BtnLoader } from "src/Loader/index";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import AreaChartBar from "src/components/AreaChart/AreaChart";

/** Calendar days in [from, to] (inclusive), YYYY-MM-DD, using local date. */
function getDaysInRange(fromISO: string, toISO: string): string[] {
  const from = new Date(fromISO);
  const to = new Date(toISO);
  const out: string[] = [];
  const cur = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  while (cur <= end) {
    const y = cur.getFullYear();
    const m = String(cur.getMonth() + 1).padStart(2, "0");
    const d = String(cur.getDate()).padStart(2, "0");
    out.push(`${y}-${m}-${d}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export default function ReportsOverviewPage() {
  const { hasRestaurant } = useAuth();
  const [datePreset, setDatePreset] = useState<ReportDatePreset>("last7");
  const range = useMemo(() => getReportDateRange(datePreset), [datePreset]);
  const input: ReportQueryInput | null = useMemo(
    () =>
      hasRestaurant?.correlationId
        ? {
            storeCorrelationId: hasRestaurant.correlationId,
            from: range.from,
            to: range.to,
          }
        : null,
    [hasRestaurant?.correlationId, range.from, range.to]
  );
  const daysInRange = useMemo(() => getDaysInRange(range.from, range.to), [range.from, range.to]);
  const prevInput: ReportQueryInput | null = useMemo(() => {
    if (!hasRestaurant?.correlationId || daysInRange.length === 0) return null;
    const fromDate = new Date(range.from);
    const fromLocal = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
    const prevToLocal = new Date(fromLocal);
    prevToLocal.setDate(prevToLocal.getDate() - 1);
    prevToLocal.setHours(23, 59, 59, 999);
    const prevFromLocal = new Date(prevToLocal);
    prevFromLocal.setDate(prevFromLocal.getDate() - (daysInRange.length - 1));
    prevFromLocal.setHours(0, 0, 0, 0);
    return {
      storeCorrelationId: hasRestaurant.correlationId,
      from: prevFromLocal.toISOString(),
      to: prevToLocal.toISOString(),
    };
  }, [hasRestaurant?.correlationId, range.from, daysInRange.length]);
  const { data, loading, error } = useReportQuery<ReportsOverviewData>(
    REPORTS_OVERVIEW,
    "reportsOverview",
    input
  );
  const { data: summaryData } = useReportQuery<ReportsSalesSummaryData>(
    REPORTS_SALES_SUMMARY,
    "reportsSalesSummary",
    input
  );
  const { data: prevSummaryData } = useReportQuery<ReportsSalesSummaryData>(
    REPORTS_SALES_SUMMARY_PREVIOUS,
    "reportsSalesSummaryPrev",
    prevInput
  );

  const chartData = useMemo(() => {
    const netByDate: Record<string, number> = {};
    for (const row of summaryData?.rows ?? []) {
      const d = row.date ?? "";
      if (d) netByDate[d] = row.netSalesCents != null ? row.netSalesCents / 100 : 0;
    }
    return daysInRange.map((dateStr) => ({
      name: dateStr,
      time: new Date(dateStr + "T00:00:00").getTime(),
      Net: netByDate[dateStr] ?? 0,
    }));
  }, [daysInRange, summaryData?.rows]);

  const cumulativeData = useMemo(() => {
    let running = 0;
    return chartData.map((point) => {
      running += point.Net;
      return {
        ...point,
        Cum: running,
        hasJump: point.Net > 0,
      };
    });
  }, [chartData]);

  const prevChartData = useMemo(() => {
    const netByDate: Record<string, number> = {};
    for (const row of prevSummaryData?.rows ?? []) {
      const d = row.date ?? "";
      if (d) netByDate[d] = row.netSalesCents != null ? row.netSalesCents / 100 : 0;
    }
    const prevDays =
      prevInput?.from != null && prevInput?.to != null
        ? getDaysInRange(prevInput.from, prevInput.to)
        : [];
    return prevDays.map((dateStr) => ({
      name: dateStr,
      time: new Date(dateStr + "T00:00:00").getTime(),
      Net: netByDate[dateStr] ?? 0,
    }));
  }, [prevInput, prevSummaryData?.rows]);

  const prevCumulative = useMemo(() => {
    let running = 0;
    return prevChartData.map((point) => {
      running += point.Net;
      return { ...point, Cum: running };
    });
  }, [prevChartData]);

  const cumulativeDataWithPrev = useMemo(() => {
    const prevCum = prevCumulative.map((p) => p.Cum);
    return cumulativeData.map((point, i) => ({
      ...point,
      PrevCum: prevCum[i] ?? null,
    }));
  }, [cumulativeData, prevCumulative]);

  const handleDateChange = (preset: ReportDatePreset, from: string, to: string) => {
    setDatePreset(preset);
  };

  const kpiRow = (
    <div className="dashboard-grid grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-[10px]">
      {[
        { label: "Total Sales", value: data?.totalSalesCents, fmt: formatCentsToDollarsWithDollarSign },
        { label: "Net Sales", value: data?.netSalesCents, fmt: formatCentsToDollarsWithDollarSign },
        { label: "Orders", value: data?.ordersCount, fmt: (v: number) => String(v ?? "—") },
        { label: "Avg Order Value", value: data?.avgOrderValueCents, fmt: formatCentsToDollarsWithDollarSign },
        { label: "Total Tips", value: data?.totalTipsCents, fmt: formatCentsToDollarsWithDollarSign },
        { label: "Labor Hours", value: data?.totalLaborHours, fmt: (v: number) => (v != null ? String(v.toFixed(1)) : "—") },
      ].map(({ label, value, fmt }) => (
        <div
          key={label}
          className="rounded-[14px] bg-white p-[10px] border border-[#f1f1f1] shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
        >
          <p className="text-[14px] text-[#6b7280] mb-1">{label}</p>
          <p className="text-lg font-semibold text-[#111827]">
            {loading ? "—" : fmt((value ?? 0) as number)}
          </p>
        </div>
      ))}
    </div>
  );

  return (
    <ReportPageLayout
      datePreset={datePreset}
      onDatePresetChange={handleDateChange}
      kpiRow={kpiRow}
      error={error}
    >
      {loading ? (
        <div className="flex justify-center items-center min-h-[200px]">
          <BtnLoader />
        </div>
      ) : (
        <>
          {chartData.length > 0 && (
            <>
              <div className="flex flex-col min-h-0 rounded-[14px] bg-white pt-4 px-4 pb-6 border border-[#f1f1f1] shadow-[0_1px_2px_rgba(0,0,0,0.04)] h-[280px] mb-6">
                <p className="shrink-0 text-sm font-medium text-[#374151] mb-5">Daily revenue</p>
                <div className="flex-1 min-h-0 w-full">
                  <AreaChartBar
                    areaChartData={{
                      data: chartData,
                      graphKeys: [{ name: "Net", strokeColor: "#8fbfa8" }],
                    }}
                    fillOpacity={0.2}
                    strokeWidth={1.5}
                    timeDataKey="time"
                    showDot
                  />
                </div>
              </div>
              <div className="flex flex-col min-h-0 rounded-[14px] bg-white pt-4 px-4 pb-6 border border-[#f1f1f1] shadow-[0_1px_2px_rgba(0,0,0,0.04)] h-[300px]">
                <p className="shrink-0 text-sm font-medium text-[#374151] mb-[18px]">Cumulative revenue</p>
                <div className="flex shrink-0 items-center gap-4 mb-2">
                  <span className="inline-flex items-center gap-1.5 text-xs text-[#6b7280]">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#9cb5a8]" aria-hidden />
                    Current period
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs text-[#6b7280]">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#c4d4ce]" aria-hidden />
                    Previous period
                  </span>
                </div>
                <div className="flex-1 min-h-0 w-full">
                  <AreaChartBar
                    areaChartData={{
                      data: cumulativeDataWithPrev,
                      graphKeys: [
                        { name: "Cum", strokeColor: "#9cb5a8" },
                        { name: "PrevCum", strokeColor: "#c4d4ce" },
                      ],
                    }}
                    fillOpacity={0.24}
                    strokeWidth={1.15}
                    timeDataKey="time"
                    showDot
                    dotPayloadKey="hasJump"
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </ReportPageLayout>
  );
}
